import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const L2Indent = () => {
  console.log("L2Indent render", Date.now());
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { role } = useSelector((state) => state.auth);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });
  
  // Use refs to prevent re-renders
  const apiEndpoint = useRef(`${API_BASE_URL}/l2page/indent`);
  const [pageData, setPageData] = useState(null);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr || dateStr === "N/A") return "N/A";
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateStr)) return dateStr;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }, []);

  const parseDDMMYY = useCallback((str) => {
    if (!str || str === "N/A") return new Date(0);
    const [day, month, year] = str.split("/");
    if (!day || !month || !year) return new Date(0);
    const fullYear = year.length === 2 ? '20' + year : year;
    return new Date(`${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const indentResponse = await axios.get(apiEndpoint.current);
      console.log('Raw indents data:', indentResponse.data);
      
      const formattedIndents = indentResponse.data.map(indent => {
        return {
          ...indent,
          L1ApprovedDate: indent.L1ApprovedDate === "N/A" ? "N/A" : formatDate(indent.L1ApprovedDate),
          L2ApprovalDate: formatDate(indent.L2ApprovalDate),
          TaskSchedule: indent.TaskSchedule || "N/A",
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending"
        };
      });
      
      formattedIndents.sort((a, b) => parseDDMMYY(b.L1ApprovedDate) - parseDDMMYY(a.L1ApprovedDate));
      
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
      setPageData(formattedIndents);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [formatDate, parseDDMMYY]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  }, []); // Empty dependency array since API_BASE_URL is constant

  // Only run once on mount
  useEffect(() => {
    fetchData();
    fetchDropdownData();
  }, []); // Empty dependency array

  const navigate = useNavigate();

  const getEditFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData.projects.map((project) => ({
        value: project.id,
        label: project.name,
      })) || [],
    },
    {
      name: "TypeID",
      label: "Type", 
      type: "select",
      options: dropdownData.types.map((type) => ({
        value: type.id,
        label: type.name,
      })) || [],
    },
  ], [dropdownData.projects, dropdownData.types]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
    } else {
      const searchTerm = query.toLowerCase().trim();
      const filtered = indents.filter((indent) => {
        const searchableFields = [
          indent.IndentID?.toString(),
          indent.Status,
          indent.ProjectName,
          indent.TypeName,
          indent.L1ApprovedDate?.toString(),
          indent.TaskSchedule
        ];
        
        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(searchTerm)
        );
      });
      setFilteredIndents(filtered);
    }
  }, [indents]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.L1ApprovedDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.L1ApprovedDate) - new Date(a.L1ApprovedDate)
        )
      );
    } else {
      setFilteredIndents(indents);
    }
  }, [indents]);

  const handleRowClick = useCallback((row) => {
    navigate(`/L2table`, { state: { row } });
  }, [navigate]);

  const tableColumns = useMemo(() => [
    { key: "IndentID", label: "Indent ID" },
    { key: "L1ApprovedDate", label: "L1 Approved Date" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Indent Type" },
    { key: "TaskSchedule", label: "Task Scheduled" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Item Count" },
  ], []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header 
        exportEndpoint={apiEndpoint.current} 
        importEndpoint={apiEndpoint.current} 
        currentIndentData={pageData}
      />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">L2 Table</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields}
            apiEndpoint={apiEndpoint.current}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchData}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            addLink="/addl2Indent"
            editRoute="/L2Table"
            searchQuery={searchQuery}
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={tableColumns}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onDoubleClick={handleRowClick}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole={role}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default L2Indent;